// =====================================
// Embedding.jsx — Embedding Generation (with persistence check)
// =====================================

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useProject } from "../ProjectContext";
import { generate_embeddings_api, cosine_similarity_api, check_embeddings_api, subscribe_task_stream } from "../api/project.api";

const Embedding = () => {
  // --- Auth and navigation ---
  const { user, setRedirectPath, userId } = useAuth();
  const { activeProject } = useProject();
  const navigate = useNavigate();

  // --- UI/logic state ---
  const [processStatus, setProcessStatus] = useState("idle"); // 'idle' | 'processing' | 'processed'
  const [infoMessage, setInfoMessage] = useState(""); // Info/status message
  const [embeddingsGenerated, setEmbeddingsGenerated] = useState(false); // Track if embeddings are generated
  const [showHeatmap, setShowHeatmap] = useState(false); // Track if heatmap should be shown
  const [imageUrl, setImageUrl] = useState(""); // Heatmap image URL
  const [similarityStatus, setSimilarityStatus] = useState("idle"); // 'idle' | 'processing' | 'generated'
  const [isCheckingExisting, setIsCheckingExisting] = useState(true); // Loading state for initial check

  const heatmapRef = useRef(null);

  // --- Auth guard ---
  useEffect(() => {
    if (!user) {
      setRedirectPath("/project/embedding");
      navigate("/login");
    }
    else if (!activeProject) {
      setRedirectPath("/project/embedding");
      navigate("/projects");
    }
  }, [user, navigate, setRedirectPath, activeProject]);

  // ========== Check for Existing Embeddings on Mount ==========
  useEffect(() => {
    const checkExistingEmbeddings = async () => {
      if (!userId || !activeProject?.ProjectID) {
        setIsCheckingExisting(false);
        return;
      }

      try {
        setIsCheckingExisting(true);
        const response = await check_embeddings_api(userId, activeProject.ProjectID);
        
        if (response.embeddings) {
          // Embeddings exist
          setProcessStatus("processed");
          setEmbeddingsGenerated(true);
          
          // Check if heatmap also exists
          if (response.imageUrl) {
            setSimilarityStatus("generated");
            setImageUrl(response.imageUrl);
            setShowHeatmap(true);
            setInfoMessage("Loaded existing embeddings and similarity heatmap.");
          } else {
            setInfoMessage("Loaded existing embeddings.");
          }
        } else {
          // No embeddings found - fresh start
          setProcessStatus("idle");
          setEmbeddingsGenerated(false);
          setSimilarityStatus("idle");
          setShowHeatmap(false);
          setImageUrl("");
        }
      } catch (error) {
        // Don't show error to user - just start fresh
        setProcessStatus("idle");
        setEmbeddingsGenerated(false);
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingEmbeddings();
  }, [userId, activeProject?.ProjectID]); // Re-check when user or project changes

  // ========== Process Embeddings Handler ==========
  const handleProcess = async () => {
    if (activeProject) {
      setProcessStatus("processing");
      setInfoMessage("");
      try {
        const start = await generate_embeddings_api(userId, activeProject.ProjectID);
        const taskId = start?.task_id;
        if (!taskId) throw new Error("No task id returned");

        // subscribe to SSE for live updates
        subscribe_task_stream(taskId, (evt) => {
          if (evt.state === 'PROCESSING') {
            setInfoMessage(evt.status || 'Processing...');
          }
          if (evt.state === 'SUCCESS') {
            setProcessStatus("processed");
            setEmbeddingsGenerated(true);
            activeProject.currentStage = "embedding";
            setInfoMessage("Embeddings generated successfully.");
          }
          if (evt.state === 'FAILURE') {
            setProcessStatus("idle");
            setInfoMessage("Embedding generation failed.");
            alert("Embedding generation failed.");
          }
        });

      } catch (error) {
        setProcessStatus("idle");
        setInfoMessage("Embedding generation failed.");
        alert("Embedding generation failed.");
      }
    }
  };

  // ========== Cosine Similarity Handler ==========
  const handleCosineSimilarity = async () => {
    setSimilarityStatus("processing");

    if (activeProject) {
      setInfoMessage("");
      try {
        const start = await cosine_similarity_api(userId, activeProject.ProjectID);
        const taskId = start?.task_id;
        if (!taskId) throw new Error("No task id returned");

        subscribe_task_stream(taskId, (evt) => {
          if (evt.state === 'PROCESSING') {
            setInfoMessage(evt.status || 'Processing similarity...');
          }
          if (evt.state === 'SUCCESS') {
            setSimilarityStatus("generated");
            const imageUrl = evt.result?.data?.imageUrl || evt.result?.imageUrl;
            if (imageUrl) {
              setImageUrl(imageUrl);
              setShowHeatmap(true);
              setInfoMessage("Similarity heatmap generated successfully.");
              activeProject.currentStage = "cosine_similarity";
            } else {
              setInfoMessage("Similarity computed but no image URL.");
            }
          }
          if (evt.state === 'FAILURE') {
            setSimilarityStatus("idle");
            setInfoMessage("HeatMap generation failed.");
            alert("HeatMap generation failed.");
          }
        });

      } catch (error) {
        setSimilarityStatus("idle");
        setInfoMessage("HeatMap generation failed.");
        alert("HeatMap generation failed.");
      }
    }
  };

  const goToUMAPCreation = () => {
    navigate("/project/umap");
  };

  // ========== Loading State ==========
  if (isCheckingExisting) {
    return (
      <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
        <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Embedding & Similarity Analysis</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#ff2e2e] mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Checking for existing data...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== Render ==========
  return (
    <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
      {/* Outlet Header - Larger */}
      <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Embedding & Similarity Analysis</h1>
          <button
            onClick={goToUMAPCreation}
            className="w-full sm:w-auto px-4 sm:px-6 md:px-9 py-2.5 sm:py-3 bg-[#ff2e2e] text-white font-bold text-sm sm:text-base md:text-lg rounded-xl shadow-[0_6px_22px_rgba(255,46,46,0.16)] hover:bg-[#e60000] transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            disabled={!embeddingsGenerated || similarityStatus !== "generated"}
          >
            UMAP Creation
          </button>
        </div>
      </div>

      {/* Content Area - fills remaining space */}
      <div className="flex-1 overflow-auto">
        {!showHeatmap ? (
          // Original expanded blocks view
          <div className="h-full flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-10">
            {/* Two Blocks Container - Stack vertically on mobile, side-by-side on lg+ */}
            <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10">

              {/* ========== Embeddings Block ========== */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[450px] lg:h-[55vh]">
                {/* Header with Title and Model */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Embeddings</h2>
                  <span className="text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded whitespace-nowrap">
                    text-embedding-3-large
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 md:mb-8 leading-relaxed flex-grow">
                  Text embeddings convert your ideas into high-dimensional vectors that capture semantic meaning.
                  These embeddings enable similarity comparisons and clustering of related concepts.
                </p>

                {/* Generate Embeddings Button */}
                <button
                  className={`
                    w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                    font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleProcess}
                  disabled={processStatus === "processing"}
                >
                  {processStatus === "idle" && "Generate Embeddings"}
                  {processStatus === "processing" && "Processing..."}
                  {processStatus === "processed" && "Embeddings Generated ✓"}
                </button>

                {/* Status Message */}
                {infoMessage && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded text-base text-blue-800">
                    {infoMessage}
                  </div>
                )}
              </div>

              {/* ========== Cosine Similarity Block ========== */}
              <div
                className={`
                  bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[450px] lg:h-[55vh]
                  ${!embeddingsGenerated ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                {/* Header with Title */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Cosine Similarity</h2>
                  {!embeddingsGenerated && (
                    <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded">
                      Locked
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 md:mb-8 leading-relaxed flex-grow">
                  Cosine similarity measures the angle between embedding vectors to determine how similar ideas are.
                  Values range from -1 to 1, where 1 indicates identical meaning and 0 indicates no relationship.
                </p>

                {/* Calculate Cosine Similarity Button */}
                <button
                  className={`
                    w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                    font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleCosineSimilarity}
                  disabled={!embeddingsGenerated || similarityStatus === "processing"}
                >
                  {similarityStatus === "idle" && "Calculate Cosine Similarity"}
                  {similarityStatus === "processing" && "Processing..."}
                  {similarityStatus === "generated" && "Cosine Similarity Generated ✓"}
                </button>

                {!embeddingsGenerated && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded text-base text-gray-600 text-center">
                    Generate embeddings first to unlock this feature
                  </div>
                )}
              </div>

            </div>
          </div>
        ) : (
          // Collapsed view with heatmap
          <div className="w-full h-full flex flex-col">
            {/* Collapsed Buttons Row */}
            <div className="w-full bg-white border-b border-gray-200 px-8 py-4">
              <div className="max-w-7xl mx-auto flex flex-wrap gap-4 justify-center">
                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleProcess}
                  disabled={processStatus === "processing"}
                >
                  {processStatus === "idle" && "Generate Embeddings"}
                  {processStatus === "processing" && "Processing..."}
                  {processStatus === "processed" && "Embeddings Generated ✓"}
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleCosineSimilarity}
                  disabled={!embeddingsGenerated || similarityStatus === "processing"}
                >
                  {similarityStatus === "idle" && "Calculate Cosine Similarity"}
                  {similarityStatus === "processing" && "Processing..."}
                  {similarityStatus === "generated" && "Cosine Similarity Generated ✓"}
                </button>
              </div>
            </div>

            {/* Heatmap Display */}
            <div ref={heatmapRef} className="flex-1 overflow-auto flex items-center justify-center p-8">
              {imageUrl && (
                <div className="w-full max-w-[900px] flex flex-col items-center">
                  <img
                    src={imageUrl}
                    alt="Cosine Distance Heatmap"
                    className="w-full max-w-[900px] rounded-[14px] shadow-[0px_8px_28px_rgba(0,0,0,0.13)]"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Embedding;
// =====================================